import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "../lib/api";
import { Trophy } from "lucide-react";
import { toast } from "sonner";

/**
 * Iter 86 — Player-facing iter3 settlement view. Read-only mirror of the
 * streamer-safe pattern, scoped strictly to the player's OWN settled
 * contracts. Displays the player-visible slice ONLY:
 *   agreed_total · verified_delivered · delta · outcome
 *   rebate rate + expected rebate + final rebate
 *
 * Endpoint: GET /api/contracts/{id}/player-settlement (positive whitelist).
 * The player never sees fee / base / non_refundable_floor / streamer
 * refund / surcharge / screening / tx / audit / admin identity.
 */

function Badge({ outcome, status }) {
  const isCancelled = status === "cancelled";
  const map = {
    shortfall: "border-[#F5B841]/60 text-[#F5B841]",
    over: "border-[#2DD4A7]/60 text-[#2DD4A7]",
    exact: "border-white/30 text-[#E8ECF4]",
  };
  if (isCancelled) return <span className="inline-flex items-center border px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest border-[#F26D6D]/50 text-[#F26D6D]">Cancelled</span>;
  return (
    <span className={`inline-flex items-center border px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ${map[outcome] || "border-white/20 text-[#8B97AE]"}`}>
      {outcome || "—"}
    </span>
  );
}

function fmt(n) {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function SettlementRow({ contract }) {
  const [sum, setSum] = useState(null);
  const [busy, setBusy] = useState(false);

  const open = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/contracts/${contract.id}/player-settlement`);
      setSum(r.data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-white/10 p-4 mb-3" data-testid={`player-settlement-row-${contract.id}`}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="font-display font-bold text-[#E8ECF4]">Contract {contract.id.slice(0, 8)}</span>
        <span className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">
          {contract.vertical} · {Number(contract.agreed_monthly_volume || 0).toLocaleString()} USD/month · 90-day term
        </span>
        {contract.settlement_outcome && <Badge outcome={contract.settlement_outcome} status={contract.status} />}
        <span className="ml-auto text-[10px] font-mono text-[#8B97AE]">{contract.status === "cancelled" ? "cancelled" : "settled"} {(contract.settled_at || contract.updatedAt || "").slice(0, 10)}</span>
      </div>
      {!sum ? (
        <div className="mt-3">
          <button
            data-testid={`player-settlement-open-${contract.id}`}
            onClick={open}
            disabled={busy}
            className="px-3 h-9 border border-[#2DD4A7]/60 text-[#2DD4A7] font-mono text-[10px] uppercase tracking-widest hover:bg-[#2DD4A7]/10 disabled:opacity-40"
          >{busy ? "Loading…" : "View settlement"}</button>
        </div>
      ) : (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs" data-testid={`player-settlement-detail-${contract.id}`}>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Agreed total</div>
            <div className="tabular-nums font-mono text-[#E8ECF4]">{fmt(sum.agreed_total)} USD</div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Verified delivered</div>
            <div className="tabular-nums font-mono text-[#E8ECF4]">{fmt(sum.verified_delivered_wager)} USD</div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Delta</div>
            <div className={`tabular-nums font-mono ${(sum.delta || 0) < 0 ? "text-[#F5B841]" : (sum.delta || 0) > 0 ? "text-[#2DD4A7]" : "text-[#E8ECF4]"}`}>{fmt(sum.delta)} USD</div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Outcome</div>
            <div><Badge outcome={sum.outcome} status={sum.status} /></div>
          </div>
          <div className="sm:col-span-4 mt-1 border border-[#2DD4A7]/40 bg-[#2DD4A7]/[0.05] p-3" data-testid={`player-settlement-rebate-${contract.id}`}>
            <div className="flex items-center gap-2">
              <Trophy className="w-4 h-4 text-[#2DD4A7]" />
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Your cashback</div>
            </div>
            <div className="mt-1 text-[#E8ECF4]">
              Rate <span className="text-[#2DD4A7]">{Math.round((sum.rebate_rate || 0) * 100)}%</span>
              &nbsp;· Expected <span className="text-[#8B97AE]">{fmt(sum.rebate_amount_expected)} USD</span>
              &nbsp;· <span className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Final</span>&nbsp;
              <span className="text-[#2DD4A7] font-mono tabular-nums">{fmt(sum.rebate_amount_final)} USD</span>
            </div>
            <div className="text-[10px] text-[#8B97AE] mt-1">Whole-deal cashback for the 90-day term (not per month). Final scales with your verified delivered volume.</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PlayerIter3Settlements() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contracts/mine");
      const settled = (Array.isArray(data) ? data : []).filter(
        (c) => c.flow === "iter3" && (c.status === "settled" || c.status === "cancelled"),
      );
      setRows(settled);
    } catch (e) {
      // silent — dashboard has other tiles
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading || rows.length === 0) return null;

  return (
    <section className="lg:col-span-12 border border-white/15 p-6 lg:p-8" data-testid="player-iter3-settlements">
      <div className="flex items-center gap-2 mb-1">
        <Trophy className="w-4 h-4 text-[#2DD4A7]" />
        <h2 className="font-display font-bold text-lg">Your settled contracts</h2>
      </div>
      <p className="text-xs text-[#8B97AE] mb-4">Read-only view of your finalized 90-day settlements and your resulting cashback.</p>
      {rows.map((c) => <SettlementRow key={c.id} contract={c} />)}
    </section>
  );
}
